/**
 * Modal "Previa do modelo", em tres abas:
 *  - Previa: como a mensagem chega, o que acontece em cada botao (link ->
 *    checkout; resposta -> vendedora e a regua para) e a ficha do modelo.
 *  - Desempenho: a escada do toque na janela do cabecalho, a posicao na
 *    categoria e a variacao contra o periodo anterior.
 *  - Historico: versoes com "ver como era" e "restaurar".
 *
 * As acoes do rodape sao LOCAIS da previa (duplicar, editar, arquivar):
 * nenhuma chama rede, e o pai avisa "na prévia" ao concluir. Arquivar nao pede
 * confirmacao porque o aviso traz "Desfazer" e nada sai do navegador; num
 * backend real, aqui entra a confirmacao.
 *
 * O pai monta este componente com `key` = id do modelo: trocar de modelo
 * reinicia a aba em vez de herdar a do anterior.
 */

import { useState } from 'react';
import { Badge, Button, CompanyLogo, Icon, Modal, Tabs, type TabItem } from '../../../components/ui';
import { formatDateTime } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { PosicaoNoGrupo } from '../desempenho';
import type { AbaDaPrevia } from '../insights';
import type { MessageTemplate, TemplateVersion } from '../types';
import {
  BOTAO_KIND_LABEL,
  CANAL_ICONE,
  CANAL_LABEL,
  CATEGORIA_LABEL,
  CATEGORIA_TOM,
  PUBLICO_DESCRICAO,
  SAIDA_LABEL,
  SETOR_LABEL,
  STATUS_DICA,
  STATUS_TOM,
  descreverPublico,
  statusLabel,
  variavelPorNome,
} from '../vocabulario';
import { DestinoDosBotoes } from './DestinoDosBotoes';
import { HistoricoVersoes } from './HistoricoVersoes';
import { PainelDesempenho } from './PainelDesempenho';
import { PreviaDoCanal } from './PreviaDoCanal';

const ID_PAINEL = 'previa-modelo-painel';

export function ModalPreviaModelo({
  template,
  empresa,
  posicao,
  periodo,
  abaInicial,
  onClose,
  onDuplicar,
  onEditar,
  onArquivar,
  onRestaurar,
}: {
  readonly template: MessageTemplate;
  readonly empresa: CompanySummary | null;
  readonly posicao: PosicaoNoGrupo | null;
  readonly periodo: string;
  readonly abaInicial: AbaDaPrevia;
  /** PRECISA ter identidade estavel (useCallback): o Modal o poe nas dependencias do efeito de foco. */
  readonly onClose: () => void;
  readonly onDuplicar: (template: MessageTemplate) => void;
  readonly onEditar: (template: MessageTemplate) => void;
  readonly onArquivar: (template: MessageTemplate) => void;
  readonly onRestaurar: (template: MessageTemplate, versao: TemplateVersion) => void;
}): JSX.Element {
  const [aba, setAba] = useState<AbaDaPrevia>(abaInicial);

  const abas: readonly TabItem<AbaDaPrevia>[] = [
    { value: 'previa', label: 'Prévia', icon: 'olho' },
    { value: 'desempenho', label: 'Desempenho', icon: 'grafico' },
    { value: 'historico', label: 'Histórico', icon: 'relogio', count: String(template.versions.length) },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Prévia do modelo"
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-mono text-ink">{template.name}</span>
          <span aria-hidden="true">·</span>
          <span>{CANAL_LABEL[template.channel]}</span>
          <span aria-hidden="true">·</span>
          <span>{descreverPublico(template)}</span>
        </span>
      }
      footer={
        <>
          <Button variant="ghost" icon="copiar" onClick={() => onDuplicar(template)}>
            Duplicar
          </Button>
          <Button variant="secondary" icon="editar" onClick={() => onEditar(template)}>
            Editar
          </Button>
          <Button variant="danger" icon="lixeira" onClick={() => onArquivar(template)}>
            Arquivar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Tabs label="Seções do modelo" items={abas} value={aba} onChange={setAba} size="sm" panelId={ID_PAINEL} />

        <div id={ID_PAINEL} role="tabpanel" aria-label={abas.find((a) => a.value === aba)?.label}>
          {aba === 'previa' ? (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-4">
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-ink">Como chega para o lead</h3>
                  <PreviaDoCanal template={template} empresa={empresa} />
                </div>
                <DestinoDosBotoes channel={template.channel} buttons={template.buttons} audience={template.audience} />
              </div>
              <Ficha template={template} empresa={empresa} />
            </div>
          ) : null}
          {aba === 'desempenho' ? <PainelDesempenho template={template} posicao={posicao} periodo={periodo} /> : null}
          {aba === 'historico' ? <HistoricoVersoes template={template} empresa={empresa} onRestaurar={(v) => onRestaurar(template, v)} /> : null}
        </div>
      </div>
    </Modal>
  );
}

function Ficha({ template, empresa }: { readonly template: MessageTemplate; readonly empresa: CompanySummary | null }): JSX.Element {
  return (
    <dl className="flex flex-col gap-4 text-sm">
      <Linha titulo="Status">
        <div className="flex flex-col items-start gap-1.5">
          <Badge tone={STATUS_TOM[template.status]} dot>
            {statusLabel(template.status, template.channel)}
          </Badge>
          <p className="text-xs text-ink-muted">{template.statusNote ?? STATUS_DICA[template.status]}</p>
        </div>
      </Linha>
      <Linha titulo="Fala com quem">
        <p className="text-ink">{descreverPublico(template)}</p>
        <p className="text-xs text-ink-muted">{PUBLICO_DESCRICAO[template.audience]} Sempre depois do fato: nenhum toque acontece antes do abandono ou do vencimento.</p>
      </Linha>
      <Linha titulo="Canal de saída">
        <span className="flex items-center gap-2 text-ink">
          <Icon name={CANAL_ICONE[template.channel]} size="sm" className="text-ink-subtle" />
          {SAIDA_LABEL[template.channel]}
        </span>
      </Linha>
      <div className="grid grid-cols-2 gap-4">
        <Linha titulo="Categoria">
          <Badge tone={CATEGORIA_TOM[template.category]}>{CATEGORIA_LABEL[template.category]}</Badge>
        </Linha>
        <Linha titulo="Setor">
          <span className="text-ink">{SETOR_LABEL[template.sector]}</span>
        </Linha>
      </div>
      <Linha titulo="Variáveis">
        {template.variableMap.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {template.variableMap.map((nome, i) => (
              <li key={`${i}-${nome}`}>
                <Badge tone="brand" className="font-mono">{`{{${i + 1}}} = ${variavelPorNome(nome)?.label ?? nome}`}</Badge>
              </li>
            ))}
          </ul>
        ) : template.variables.length === 0 ? (
          <span className="text-ink-subtle">Nenhuma — o texto sai igual para todo mundo.</span>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {template.variables.map((v) => (
              <li key={v}>
                <Badge tone="brand" className="font-mono">{`{${v}}`}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Linha>
      {template.channel === 'whatsapp_oficial' || template.channel === 'email' ? (
        <Linha titulo={template.channel === 'email' ? 'Botão de ação' : 'Botões'}>
          {template.buttons.length === 0 ? (
            <span className="text-ink-subtle">Sem botões.</span>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {template.buttons.map((b, i) => (
                <li key={`${b.label}-${i}`} className="flex flex-wrap items-center gap-2">
                  <Icon name={b.kind === 'link' ? 'externo' : 'resposta'} size="xs" className="text-ink-subtle" />
                  <span className="font-medium text-ink">{b.label}</span>
                  <span className="text-xs text-ink-subtle">
                    {BOTAO_KIND_LABEL[b.kind]}
                    {b.kind === 'link' && b.url !== undefined ? ` · ${b.url}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Linha>
      ) : null}
      <Linha titulo="Empresa">
        {empresa === null ? (
          <Badge tone="brand" icon="empresa">
            Global — vale para todas as empresas
          </Badge>
        ) : (
          <span className="flex items-center gap-2 text-ink">
            <CompanyLogo company={empresa} size="sm" />
            {empresa.name}
          </span>
        )}
      </Linha>
      <Linha titulo="Usado em">
        {template.usedIn.length === 0 ? (
          <span className="text-ink-subtle">Não está ligado a nenhuma régua ou disparo.</span>
        ) : (
          <ul className="flex flex-col gap-1 text-ink">
            {template.usedIn.map((uso) => (
              <li key={uso} className="flex items-center gap-2">
                <Icon name="raio" size="xs" className="text-accent" />
                {uso}
              </li>
            ))}
          </ul>
        )}
      </Linha>
      <Linha titulo="Atualizado em">
        <span className="text-ink">{formatDateTime(template.updatedAt)}</span>
      </Linha>
    </dl>
  );
}

function Linha({ titulo, children }: { readonly titulo: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-subtle">{titulo}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
